import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, SelectField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { api, errorMessage } from '../../api.js';
import { useI18n, type I18n } from '../../i18n.js';
import { dinarsToMinor } from './CarriersPage.js';

type Order = Schemas['Order'];
type Shipment = Schemas['Shipment'];
type Carrier = Schemas['Carrier'];

function shipmentError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError && failure.code === 'validation-failed') {
    return t('admin.shipment.error.validation');
  }
  return errorMessage(t, failure);
}

/** Quantity of each order line still to ship (returned parcels give their items back). */
export function leftToShip(order: Order): Map<string, number> {
  const left = new Map(order.lines.map((line) => [line.orderLineId, line.quantity]));
  for (const shipment of order.shipments) {
    if (shipment.status === 'returned') continue;
    for (const line of shipment.lines) {
      left.set(line.orderLineId, (left.get(line.orderLineId) ?? 0) - line.quantity);
    }
  }
  return left;
}

/**
 * Parcels of the order: list with tracking, cash to collect, carrier fee, delivery notes and
 * delivered/returned buttons; form to ship the remaining items (all or some).
 */
export function ShipmentsSection({
  storeId,
  order,
  canManage,
  onChange,
}: {
  readonly storeId: string;
  readonly order: Order;
  readonly canManage: boolean;
  readonly onChange: (order: Order) => Promise<void>;
}) {
  const { t, formatPrice, formatDateTime } = useI18n();
  const orderId = order.orderId;
  const [shipments, setShipments] = useState<Shipment[]>([]);
  // null until loaded: the shipping form proposes the first carrier once it is known.
  const [carriers, setCarriers] = useState<Carrier[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [list, carrierList] = await Promise.all([
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}/shipments', {
          params: { path: { storeId, orderId } },
        }),
      ),
      unwrap(api.GET('/admin-api/v1/stores/{storeId}/carriers', { params: { path: { storeId } } })),
    ]);
    setShipments(list.items);
    setCarriers(carrierList.items);
  }, [storeId, orderId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t, order.version]);

  async function setStatus(shipment: Shipment, status: 'delivered' | 'returned') {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const next = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/shipments/{shipmentId}/status', {
          params: { path: { storeId, shipmentId: shipment.shipmentId } },
          body: { version: shipment.version, status },
        }),
      );
      setNotice(t('admin.shipment.updated'));
      await onChange(next);
    } catch (failure) {
      setError(shipmentError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  const left = leftToShip(order);
  const canShip =
    canManage &&
    (order.status === 'confirmed' || order.status === 'shipped') &&
    [...left.values()].some((quantity) => quantity > 0);
  const titleOf = (orderLineId: string) => {
    const line = order.lines.find((item) => item.orderLineId === orderLineId);
    if (line === undefined) return '';
    return line.variantLabel === null ? line.title : `${line.title} (${line.variantLabel})`;
  };

  return (
    <Card labelledBy="order-shipments">
      <h2 id="order-shipments" className="mrigl-subheading">
        {t('admin.shipment.title')}
      </h2>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {shipments.length === 0 ? <p className="mrigl-muted">{t('admin.shipment.none')}</p> : null}
      {shipments.map((shipment) => {
        const number = String(shipment.number);
        return (
          <section
            key={shipment.shipmentId}
            className="mrigl-stack admin-shipment"
            aria-label={t('admin.shipment.number', { number })}
          >
            <h3 className="mrigl-subheading">
              {t('admin.shipment.number', { number })} —{' '}
              {t(`admin.shipment.status.${shipment.status}`)}
            </h3>
            <p>
              <span dir="auto">{shipment.carrierName ?? t('admin.shipment.ownDelivery')}</span>
              {shipment.trackingNumber === null ? null : (
                <>
                  {' — '}
                  {shipment.trackingUrl === null ? (
                    <span dir="ltr">
                      {t('admin.shipment.tracking', { tracking: shipment.trackingNumber })}
                    </span>
                  ) : (
                    <a href={shipment.trackingUrl} target="_blank" rel="noreferrer noopener">
                      {t('admin.shipment.tracking', { tracking: shipment.trackingNumber })}
                    </a>
                  )}
                </>
              )}{' '}
              — <span className="mrigl-muted">{formatDateTime(shipment.shippedAt)}</span>
            </p>
            <ul className="mrigl-list">
              {shipment.lines.map((line) => (
                <li key={line.orderLineId} dir="auto">
                  {line.quantity} × {titleOf(line.orderLineId)}
                </li>
              ))}
            </ul>
            <p>
              {t('admin.shipment.cod', { amount: formatPrice(shipment.codAmount.minor) })}
              {shipment.fee === null
                ? null
                : ` — ${t('admin.shipment.fee', { amount: formatPrice(shipment.fee.minor) })}`}
            </p>
            <p className="admin-actions">
              {(['fr', 'ar'] as const).map((language) => (
                <a
                  key={language}
                  href={`/admin-api/v1/stores/${storeId}/shipments/${shipment.shipmentId}/delivery-note.pdf?locale=${language}`}
                  download
                >
                  {t('admin.shipment.note', {
                    number,
                    language: t(`admin.language.${language}`),
                  })}
                </a>
              ))}
            </p>
            {canManage && shipment.status !== 'returned' ? (
              <p className="admin-actions">
                {shipment.status === 'shipped' ? (
                  <Button
                    variant="secondary"
                    busy={busy}
                    onClick={() => void setStatus(shipment, 'delivered')}
                  >
                    {t('admin.shipment.markDelivered', { number })}
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  busy={busy}
                  onClick={() => void setStatus(shipment, 'returned')}
                >
                  {t('admin.shipment.markReturned', { number })}
                </Button>
              </p>
            ) : null}
          </section>
        );
      })}
      {canShip && carriers !== null ? (
        <ShipForm
          storeId={storeId}
          order={order}
          left={left}
          carriers={carriers.filter((carrier) => carrier.active)}
          titleOf={titleOf}
          onShipped={async (next) => {
            setNotice(t('admin.shipment.created'));
            await onChange(next);
          }}
        />
      ) : null}
    </Card>
  );
}

function ShipForm({
  storeId,
  order,
  left,
  carriers,
  titleOf,
  onShipped,
}: {
  readonly storeId: string;
  readonly order: Order;
  readonly left: Map<string, number>;
  readonly carriers: readonly Carrier[];
  readonly titleOf: (orderLineId: string) => string;
  readonly onShipped: (order: Order) => Promise<void>;
}) {
  const { t } = useI18n();
  const remaining = order.lines.filter((line) => (left.get(line.orderLineId) ?? 0) > 0);
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      remaining.map((line) => [line.orderLineId, String(left.get(line.orderLineId) ?? 0)]),
    ),
  );
  const [carrierId, setCarrierId] = useState(carriers[0]?.carrierId ?? '');
  const [tracking, setTracking] = useState('');
  const [fee, setFee] = useState('');
  const [cod, setCod] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cash = order.paymentMethod === 'cash_on_delivery';

  async function ship(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const lines = Object.entries(quantities)
      .map(([orderLineId, value]) => ({ orderLineId, quantity: Number(value) }))
      .filter((line) => Number.isInteger(line.quantity) && line.quantity > 0);
    if (lines.length === 0) {
      setError(t('admin.shipment.error.empty'));
      return;
    }
    const feeMinor = fee.trim() === '' ? undefined : dinarsToMinor(fee);
    const codMinor = cod.trim() === '' ? undefined : dinarsToMinor(cod);
    if (feeMinor === null || codMinor === null) {
      setError(t('admin.shipment.error.validation'));
      return;
    }
    setBusy(true);
    try {
      const next = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/orders/{orderId}/shipments', {
          params: { path: { storeId, orderId: order.orderId } },
          body: {
            version: order.version,
            lines,
            carrierId: carrierId === '' ? null : carrierId,
            trackingNumber: tracking.trim() === '' ? null : tracking.trim(),
            ...(feeMinor === undefined ? {} : { fee: { minor: feeMinor, currency: 'TND' } }),
            ...(codMinor === undefined ? {} : { codAmount: { minor: codMinor, currency: 'TND' } }),
          },
        }),
      );
      await onShipped(next);
    } catch (failure) {
      setError(shipmentError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.shipment.new')}
      onSubmit={(event) => void ship(event)}
    >
      <h3 className="mrigl-subheading">{t('admin.shipment.new')}</h3>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {remaining.map((line) => (
        <TextField
          key={line.orderLineId}
          type="number"
          min={0}
          max={left.get(line.orderLineId) ?? 0}
          step={1}
          label={t('admin.shipment.quantity', { item: titleOf(line.orderLineId) })}
          hint={t('admin.shipment.left', { count: String(left.get(line.orderLineId) ?? 0) })}
          value={quantities[line.orderLineId] ?? ''}
          onChange={(event) => {
            setQuantities({ ...quantities, [line.orderLineId]: event.target.value });
          }}
        />
      ))}
      <SelectField
        label={t('admin.shipment.carrier')}
        value={carrierId}
        onChange={(event) => {
          setCarrierId(event.target.value);
        }}
        options={[
          ...carriers.map((carrier) => ({ value: carrier.carrierId, label: carrier.name })),
          { value: '', label: t('admin.shipment.ownDelivery') },
        ]}
      />
      <TextField
        label={t('admin.shipment.trackingNumber')}
        dir="ltr"
        maxLength={60}
        value={tracking}
        onChange={(event) => {
          setTracking(event.target.value);
        }}
      />
      <TextField
        label={t('admin.shipment.feeLabel')}
        hint={t('admin.shipment.feeHint')}
        inputMode="decimal"
        dir="ltr"
        value={fee}
        onChange={(event) => {
          setFee(event.target.value);
        }}
      />
      {cash ? (
        <TextField
          label={t('admin.shipment.codLabel')}
          hint={t('admin.shipment.codHint')}
          inputMode="decimal"
          dir="ltr"
          value={cod}
          onChange={(event) => {
            setCod(event.target.value);
          }}
        />
      ) : null}
      <Button type="submit" busy={busy}>
        {t('admin.shipment.create')}
      </Button>
    </form>
  );
}
